import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateCustomIdsForList } from '../utils/customIdGenerator';
import { 
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  DocumentIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';

const TrademarkApplicationsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // TM-A automation state
  const [tmAModal, setTmAModal] = useState(null); // { application }
  const [tmATrademarkIndex, setTmATrademarkIndex] = useState(0);
  const [tmAClassIndex, setTmAClassIndex] = useState(0);
  const [tmAFiling, setTmAFiling] = useState(false);
  const [tmATaskId, setTmATaskId] = useState(null);
  const [tmAStatus, setTmAStatus] = useState('');
  const [tmAEnded, setTmAEnded] = useState(false);

  useEffect(() => {
    loadApplications();
  }, [search, statusFilter, currentPage]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10
      });

      if (search) {
        params.append('search', search);
      }

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await api.get(`/trademark-applications?${params}`);
      const applicationsWithCustomIds = await generateCustomIdsForList('TrademarkApplication', response.data.data);
      setApplications(applicationsWithCustomIds);
      setTotalPages(response.data.pagination.pages);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to load applications:', error);
      toast.error('Failed to load trademark applications');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      pending: { color: 'bg-blue-100 text-blue-800', icon: ClockIcon },
      submitted: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      rejected: { color: 'bg-red-100 text-red-800', icon: ExclamationTriangleIcon },
      filed: { color: 'bg-purple-100 text-purple-800', icon: DocumentIcon }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getFilingTypeBadge = (filingType) => {
    const typeConfig = {
      'Individual': 'bg-gray-100 text-gray-800',
      'Small Enterprise': 'bg-blue-100 text-blue-800',
      'Startup': 'bg-green-100 text-green-800',
      'Others': 'bg-purple-100 text-purple-800',
      'International': 'bg-orange-100 text-orange-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig[filingType] || typeConfig.Others}`}>
        {filingType}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Search masterData.documents Map (serialised as object) for a keyword
  // Search masterData.documents Map (keys are user-defined labels like "Trademark POA", "Udyog_Aadhaar")
  const findInDocumentsMap = (documents, ...keywords) => {
    if (!documents || typeof documents !== 'object') return false;
    const entries = documents instanceof Map
      ? [...documents.entries()]
      : Object.entries(documents);
    return entries.some(([key, val]) => {
      const lk = (key || '').toLowerCase().replace(/_/g, ' ');
      return keywords.some(kw => lk.includes(kw.toLowerCase().replace(/_/g, ' '))) &&
        !!(val?.fileUrl || val?.documentUrl || val?.documentId);
    });
  };

  // Returns list of { label, ok, required } for the selected trademark+class
  const getDocumentChecks = (application, trademarkIndex, classIndex) => {
    const masterData = application?.masterDataId;
    const trademark = application?.trademarks?.[trademarkIndex];
    const classItem = trademark?.classes?.[classIndex];
    const docsMap = masterData?.documents;

    // application.documents keys are uppercase after backend transform: POWER_OF_ATTORNEY, UDYOG_AADHAAR
    const appPOA = application?.documents?.POWER_OF_ATTORNEY || application?.documents?.powerOfAttorney;
    const appUdyog = application?.documents?.UDYOG_AADHAAR || application?.documents?.udyogAadhaar;

    // Check all three sources: application.documents, masterData.trademarkDocuments, masterData.documents map
    const hasPOA = !!(
      appPOA?.uploaded ||
      appPOA?.documentId ||
      masterData?.trademarkDocuments?.powerOfAttorney?.documentId
    ) || findInDocumentsMap(docsMap, 'trademark poa', 'poa', 'power of attorney', 'power_of_attorney');

    const needsUserAffidavit = classItem?.useOfMark === 'In use';
    const hasUserAffidavit = !!(
      classItem?.userAffidavitUrl || classItem?.userAffidavitDocumentId
    );

    const hasSmallEnterprise = !!(
      appUdyog?.uploaded ||
      appUdyog?.documentId ||
      masterData?.trademarkDocuments?.udyogAadhaar?.documentId
    ) || findInDocumentsMap(docsMap, 'udyog aadhaar', 'udyog', 'aadhaar', 'small enterprise', 'small_enterprise');

    const checks = [
      {
        label: 'Authorization Document (POA)',
        ok: hasPOA,
        required: true,
      },
      {
        label: `User Affidavit${needsUserAffidavit ? ' (required — mark is In use)' : ' (not required — mark is Proposed to be used)'}`,
        ok: needsUserAffidavit ? hasUserAffidavit : true,
        required: needsUserAffidavit,
        skipped: !needsUserAffidavit,
      },
      {
        label: 'Small Enterprise Document (Udyog Aadhaar)',
        ok: hasSmallEnterprise,
        required: true,
      },
    ];

    return checks;
  };

  /** Compact doc status for inline display per class row */
  const getDocCheckSummary = (application, tmIdx, clsIdx) => {
    const checks = getDocumentChecks(application, tmIdx, clsIdx);
    const required = checks.filter(c => c.required);
    const ok = required.filter(c => c.ok).length;
    return { ok, total: required.length, allOk: ok === required.length, checks };
  };

  const handleScheduleToQueue = async (tmIdx, clsIdx) => {
    if (!tmAModal?.application) return;
    const appId = tmAModal.application._id;
    try {
      await api.post('/filing-queue', {
        applicationId: appId,
        trademarkIndex: tmIdx,
        classIndex: clsIdx,
      });
      toast.success('Added to queue — filing will start automatically');
      const response = await api.get(`/trademark-applications/${appId}`);
      setTmAModal(prev => ({ ...prev, application: response.data.data, loading: false }));
      loadApplications();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to schedule');
    }
  };

  const openTmAModal = async (application) => {
    // Open modal immediately with basic info, then load full data
    setTmAModal({ application, loading: true });
    setTmATrademarkIndex(0);
    setTmAClassIndex(0);
    setTmAStatus('');
    setTmAEnded(false);
    setTmATaskId(null);
    setTmAFiling(false);

    try {
      const response = await api.get(`/trademark-applications/${application._id}`);
      const fullApplication = response.data.data;
      setTmAModal({ application: fullApplication, loading: false });
    } catch (e) {
      console.error('Failed to load application details', e);
      setTmAModal({ application, loading: false });
    }
  };

  // tmIdx / clsIdx are passed directly so we don't depend on stale state during the poll
  const handleFileTmA = async (tmIdx, clsIdx) => {
    if (!tmAModal) return;
    const appId = tmAModal.application._id;
    const trademarkIndex = tmIdx !== undefined ? tmIdx : tmATrademarkIndex;
    const classIndex    = clsIdx !== undefined ? clsIdx : tmAClassIndex;

    setTmATrademarkIndex(trademarkIndex);
    setTmAClassIndex(classIndex);

    // Pre-flight document check
    const checks = getDocumentChecks(tmAModal.application, trademarkIndex, classIndex);
    const missing = checks.filter(c => c.required && !c.ok);
    if (missing.length > 0) {
      toast.error(`Missing required documents: ${missing.map(c => c.label).join(', ')}`);
      return;
    }

    try {
      setTmAFiling(true);
      setTmAStatus('Starting TM-A automation...\n');
      setTmAEnded(false);
      setTmATaskId(null);

      const response = await api.post(`/trademark-applications/${appId}/file-tma`, {
        trademarkIndex,
        classIndex,
      });

      const { taskId } = response.data;
      setTmATaskId(taskId);
      setTmAStatus(prev => prev + `Task started: ${taskId}\n`);

      const poll = setInterval(async () => {
        try {
          const statusResp = await api.post(`/trademark-applications/${appId}/tma-status`, {
            taskId,
            trademarkIndex,
            classIndex,
          });
          const data = statusResp.data.data;
          if (data?.status) setTmAStatus(data.status);
          if (data?.ended) {
            clearInterval(poll);
            setTmAEnded(true);
            setTmAFiling(false);
            // Refresh list so per-class ipindiaReference is reflected
            loadApplications();
            if (data.success === 'Success' || data.success === true) {
              toast.success(`TM-A filed! Application No: ${data.applicationNo || data.applicationNumber || ''}`);
            } else {
              toast.error('TM-A automation ended with errors. Check the log.');
            }
          }
        } catch (e) {
          clearInterval(poll);
          setTmAFiling(false);
          setTmAEnded(true);
        }
      }, 2000);
    } catch (error) {
      console.error('TM-A filing failed:', error);
      toast.error(error.response?.data?.message || 'Failed to start TM-A automation');
      setTmAFiling(false);
      setTmAEnded(true);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadApplications();
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status === statusFilter ? '' : status);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trademark Applications</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage and track all trademark applications
          </p>
        </div>
        <button
          onClick={() => navigate('/trademark-applications/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Application
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search applications..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </form>

          {/* Status Filters */}
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-500">Filter by status:</span>
            <div className="flex space-x-1">
              {['draft', 'pending', 'submitted', 'approved', 'rejected', 'filed'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-3 py-1 text-xs font-medium rounded-full ${
                    statusFilter === status
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Custom ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Application
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Filing Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trademarks
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Fees
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.map((application) => (
                <tr key={application._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {application.customId || application._id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {application.applicationNumber}
                      </div>
                      <div className="text-sm text-gray-500">
                        {application.trademarks.length} trademark(s)
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {application.masterDataId?.clientName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {application.masterDataId?.country || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getFilingTypeBadge(application.filingType)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {application.trademarks.length} trademark(s)
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {application.trademarks.slice(0, 2).map((trademark, index) => (
                        <div key={index} className="mb-1">
                          <span className="font-medium">{trademark.trademarkName}</span>
                          {trademark.className && (
                            <span className="ml-1">• Class {trademark.class} - {trademark.className}</span>
                          )}
                        </div>
                      ))}
                      {application.trademarks.length > 2 && (
                        <div className="text-gray-400">+{application.trademarks.length - 2} more</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(application.totalFees)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(application.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(application.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/trademark-applications/${application._id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Application"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {application.status === 'draft' && (
                        <button
                          onClick={() => navigate(`/trademark-applications/${application._id}`)}
                          className="text-green-600 hover:text-green-900"
                          title="Edit Application"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      {isAdmin && (() => {
                        const allClasses = application.trademarks?.flatMap(tm => tm.classes || []) || [];
                        const filedCount = allClasses.filter(c => c.ipindiaReference).length;
                        const totalCount = allClasses.length;
                        const allFiled = totalCount > 0 && filedCount === totalCount;
                        return (
                          <button
                            onClick={() => openTmAModal(application)}
                            className={`px-2 py-1 text-xs rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 ${allFiled ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                            title="TM-A Filing Status"
                          >
                            {allFiled ? `✓ Filed (${filedCount})` : filedCount > 0 ? `File TM-A (${filedCount}/${totalCount} filed)` : 'File TM-A'}
                          </button>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {applications.length === 0 && (
          <div className="text-center py-12">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No applications found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating a new trademark application.'}
            </p>
            {!search && !statusFilter && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/trademark-applications/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  New Application
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow-sm">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * 10 + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * 10, total)}
                </span>{' '}
                of <span className="font-medium">{total}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* TM-A Automation Modal */}
      {tmAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">File TM-A via Automation</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {tmAModal.application.applicationNumber} — {tmAModal.application.masterDataId?.clientName || ''}
                </p>
              </div>
              <button
                onClick={() => setTmAModal(null)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {tmAModal.loading ? (
                <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500 mr-2"></div>
                  Loading application data...
                </div>
              ) : tmAModal.application.trademarks?.length > 0 ? (
                <>
                  {tmAModal.application.isLocked && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                      <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                      Locked: {tmAModal.application.lockedReason || 'Scheduled or filing in progress'}
                    </div>
                  )}
                  {tmAModal.application.trademarks.map((tm, tmIdx) => (
                    <div key={tmIdx} className="border border-gray-200 rounded-md overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                        <span className="text-sm font-semibold text-gray-800">{tm.trademarkName || `Trademark ${tmIdx + 1}`}</span>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {(tm.classes || []).map((cls, clsIdx) => {
                            const isFilingThis = tmAFiling && tmATrademarkIndex === tmIdx && tmAClassIndex === clsIdx;
                            const docSummary = getDocCheckSummary(tmAModal.application, tmIdx, clsIdx);
                            const hasMissing = !docSummary.allOk;
                            const locked = tmAModal.application.isLocked;
                            return (
                              <React.Fragment key={clsIdx}>
                                <tr className="border-t border-gray-100">
                                  <td className="px-3 py-2 font-medium text-gray-700 w-24">Class {cls.class}</td>
                                  <td className="px-3 py-2 text-gray-500 text-xs">{cls.classDescription?.substring(0, 50)}{cls.classDescription?.length > 50 ? '…' : ''}</td>
                                  <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <div className="flex flex-col items-end gap-1">
                                      {cls.ipindiaReference ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                          ✓ {cls.ipindiaReference}
                                        </span>
                                      ) : isFilingThis ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-indigo-600 font-medium">
                                          <span className="animate-spin h-3 w-3 border border-indigo-500 border-t-transparent rounded-full"></span>
                                          Filing…
                                        </span>
                                      ) : locked ? (
                                        <span className="text-xs text-amber-600">Locked</span>
                                      ) : (
                                        <div className="flex gap-1">
                                          <button
                                            onClick={() => handleFileTmA(tmIdx, clsIdx)}
                                            disabled={tmAFiling || hasMissing}
                                            title={hasMissing ? 'Missing required documents' : 'File now'}
                                            className="px-2 py-0.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40"
                                          >
                                            File TM-A
                                          </button>
                                          <button
                                            onClick={() => handleScheduleToQueue(tmIdx, clsIdx)}
                                            disabled={tmAFiling || hasMissing}
                                            title="Add to scheduler queue"
                                            className="px-2 py-0.5 text-xs border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50 disabled:opacity-40"
                                          >
                                            Schedule
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                <tr className="bg-gray-50/80">
                                  <td colSpan={3} className="px-3 py-1.5">
                                    <div className="flex flex-wrap gap-2 text-xs">
                                      {docSummary.checks.map((c, i) => (
                                        <span
                                          key={i}
                                          className={c.skipped ? 'text-gray-400' : c.ok ? 'text-green-700' : 'text-red-600 font-medium'}
                                        >
                                          {c.skipped ? 'Affidavit: N/A' : c.label.includes('POA') ? `POA: ${c.ok ? '✓' : '✗'}` :
                                            c.label.includes('Affidavit') ? `Affidavit: ${c.ok ? '✓' : '✗'}` :
                                            `Udyog: ${c.ok ? '✓' : '✗'}`}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}

                  {/* Live log */}
                  {tmAStatus && (
                    <div className="bg-gray-900 text-green-400 text-xs font-mono p-3 rounded-md h-40 overflow-y-auto whitespace-pre-wrap">
                      {tmAStatus}
                    </div>
                  )}
                  {tmAEnded && (
                    <p className="text-sm text-center text-gray-500">Automation finished. Check the log above for results.</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No trademarks found on this application.</p>
              )}
            </div>

            <div className="flex justify-between items-center p-4 border-t">
              <a
                href="/trademark-filing-queue"
                className="text-sm text-indigo-600 hover:underline"
                onClick={e => { e.preventDefault(); navigate('/trademark-filing-queue'); }}
              >
                Open Filing Scheduler →
              </a>
              <button
                onClick={() => setTmAModal(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrademarkApplicationsList; 